import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import { post } from "../../../services/smartApiService";
import { OPTIONS_URLS } from "../../../api/OptionsUrls";

interface ChatMessage {
  id: number;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  contextIncluded?: { option_chain: boolean; technical: boolean };
}

interface OptionChainRow {
  strike: number;
  callOI: number;
  callVolume: number;
  callLTP: number;
  callIV: string;
  callBid: number;
  callAsk: number;
  putOI: number;
  putVolume: number;
  putLTP: number;
  putIV: string;
  putBid: number;
  putAsk: number;
}

interface OIChangeItem {
  strike: number;
  callOIChange: number;
  putOIChange: number;
}

interface OptionsChatBotProps {
  symbol: string;
  spotPrice: number;
  pcr: number;
  optionChain: OptionChainRow[];
  technicalIndicators: any;
  expiryDate: string;
  oiChangeData?: OIChangeItem[];
  confluenceAlerts?: { message: string; type: string }[];
}

const OptionsChatBot: React.FC<OptionsChatBotProps> = ({
  symbol,
  spotPrice,
  pcr,
  optionChain,
  technicalIndicators,
  expiryDate,
  oiChangeData = [],
  confluenceAlerts = [],
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [includeOC, setIncludeOC] = useState(false);
  const [includeTech, setIncludeTech] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const msgIdRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg: ChatMessage = {
      id: ++msgIdRef.current,
      role: "user",
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    // Send last 6 messages as conversation history for context
    const recentHistory = messages.slice(-6).map(m => ({
      role: m.role,
      content: m.content,
    }));

    const payload: any = {
      message: trimmed,
      symbol,
      include_option_chain: includeOC,
      include_technical: includeTech,
      expiry_date: expiryDate,
      conversation_history: recentHistory.length > 0 ? recentHistory : undefined,
    };

    if (includeOC && optionChain.length > 0) {
      // Send only ±10 strikes around ATM (not the full 40-80 strikes)
      const sorted = [...optionChain].sort((a, b) => a.strike - b.strike);
      const atmIdx = sorted.findIndex(r => r.strike >= spotPrice);
      const start = Math.max(0, atmIdx - 10);
      const end = Math.min(sorted.length, atmIdx + 11);
      const trimmedChain = sorted.slice(start, end);

      // Merge OI change data into chain
      const oiChangeMap = new Map(oiChangeData.map(d => [d.strike, d]));
      payload.option_chain = trimmedChain.map(row => ({
        ...row,
        callOIChange: oiChangeMap.get(row.strike)?.callOIChange || 0,
        putOIChange: oiChangeMap.get(row.strike)?.putOIChange || 0,
      }));
      payload.spot_price = spotPrice;
      payload.pcr = pcr;

      // Include active confluence alerts for additional context
      if (confluenceAlerts.length > 0) {
        payload.confluence_alerts = confluenceAlerts.map(a => a.message);
      }
    }

    if (includeTech && technicalIndicators) {
      payload.technical_indicators = technicalIndicators;
    }

    try {
      const response: any = await post(OPTIONS_URLS.CHAT, payload, {
        showLoading: false,
      }).toPromise();

      if (response?.data?.response) {
        const botMsg: ChatMessage = {
          id: ++msgIdRef.current,
          role: "assistant",
          content: response.data.response,
          timestamp: new Date(),
          contextIncluded: response.data.context_included,
        };
        setMessages((prev) => [...prev, botMsg]);
      }
    } catch {
      const errorMsg: ChatMessage = {
        id: ++msgIdRef.current,
        role: "assistant",
        content: "Sorry, unable to get a response. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, symbol, spotPrice, pcr, optionChain, technicalIndicators, includeOC, includeTech, expiryDate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickPrompts = [
    { label: "Support/Resistance", prompt: "What are the key support and resistance levels based on current OI data?" },
    { label: "OI Analysis", prompt: "Analyze the current OI buildup. Where is fresh writing happening?" },
    { label: "Trade Setup", prompt: "Suggest a good option strategy for current market conditions with entry, target, and stoploss." },
    { label: "Market View", prompt: "What is the overall market sentiment? Bullish, bearish, or sideways?" },
  ];

  return (
    <>
    <style>{`
      .chat-markdown p { margin: 0.2rem 0; }
      .chat-markdown h1, .chat-markdown h2, .chat-markdown h3, .chat-markdown h4 {
        color: #e0e3eb; margin: 0.4rem 0 0.2rem; font-size: 0.75rem;
      }
      .chat-markdown h3 { font-size: 0.72rem; }
      .chat-markdown strong { color: #e0e3eb; }
      .chat-markdown em { color: #ffd54f; font-style: italic; }
      .chat-markdown ul, .chat-markdown ol { margin: 0.2rem 0; padding-left: 1.2rem; }
      .chat-markdown li { margin: 0.1rem 0; }
      .chat-markdown code { background: rgba(255,255,255,0.08); padding: 0.1rem 0.3rem; border-radius: 3px; font-size: 0.65rem; }
      .chat-markdown hr { border: none; border-top: 1px solid #363a45; margin: 0.3rem 0; }
      .chat-markdown a { color: #2962ff; text-decoration: none; }
    `}</style>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "620px",
        backgroundColor: "#1e222d",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "0.4rem 0.6rem",
          borderBottom: "1px solid #2b2b43",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ color: "#d1d4dc", fontWeight: 600, fontSize: "0.75rem" }}>
          AI Assistant
        </span>
        <div style={{ display: "flex", gap: "0.25rem" }}>
          <button
            onClick={() => setMessages([])}
            style={{
              background: "none",
              border: "1px solid #363a45",
              color: "#787b86",
              fontSize: "0.6rem",
              padding: "0.1rem 0.35rem",
              borderRadius: "3px",
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Context Toggles */}
      <div
        style={{
          padding: "0.3rem 0.6rem",
          borderBottom: "1px solid #2b2b43",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <label
          style={{
            fontSize: "0.65rem",
            color: includeOC ? "#26a69a" : "#787b86",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.2rem",
          }}
        >
          <input
            type="checkbox"
            checked={includeOC}
            onChange={(e) => setIncludeOC(e.target.checked)}
            style={{ width: "12px", height: "12px" }}
          />
          Option Chain
        </label>
        <label
          style={{
            fontSize: "0.65rem",
            color: includeTech ? "#2962ff" : "#787b86",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.2rem",
          }}
        >
          <input
            type="checkbox"
            checked={includeTech}
            onChange={(e) => setIncludeTech(e.target.checked)}
            style={{ width: "12px", height: "12px" }}
          />
          Technical
        </label>
        {spotPrice > 0 && (
          <span style={{ fontSize: "0.6rem", color: "#787b86", marginLeft: "auto" }}>
            {symbol} {spotPrice.toFixed(0)} | PCR {pcr.toFixed(2)}
          </span>
        )}
      </div>

      {/* Messages Area */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "0.4rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.4rem",
        }}
      >
        {messages.length === 0 && (
          <div style={{ padding: "1rem 0.5rem", textAlign: "center" }}>
            <p style={{ color: "#787b86", fontSize: "0.7rem", marginBottom: "0.75rem" }}>
              Ask about {symbol} options, OI analysis, strategies, or market outlook.
            </p>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.3rem",
              }}
            >
              {quickPrompts.map((qp, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput(qp.prompt);
                    setIncludeOC(true);
                    setIncludeTech(true);
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                  style={{
                    padding: "0.35rem 0.5rem",
                    fontSize: "0.65rem",
                    backgroundColor: "rgba(41, 98, 255, 0.1)",
                    border: "1px solid #2b2b43",
                    borderRadius: "4px",
                    color: "#2962ff",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  {qp.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            style={{
              alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "90%",
              padding: "0.35rem 0.5rem",
              borderRadius: msg.role === "user" ? "8px 8px 0 8px" : "8px 8px 8px 0",
              backgroundColor:
                msg.role === "user"
                  ? "rgba(41, 98, 255, 0.2)"
                  : "rgba(255, 255, 255, 0.05)",
              border:
                msg.role === "user"
                  ? "1px solid rgba(41, 98, 255, 0.3)"
                  : "1px solid #2b2b43",
            }}
          >
            <div
              className={msg.role === "assistant" ? "chat-markdown" : ""}
              style={{
                fontSize: "0.7rem",
                color: "#d1d4dc",
                wordBreak: "break-word",
                lineHeight: "1.5",
                ...(msg.role === "user" ? { whiteSpace: "pre-wrap" } : {}),
              }}
            >
              {msg.role === "assistant" ? (
                <ReactMarkdown>{msg.content}</ReactMarkdown>
              ) : (
                msg.content
              )}
            </div>
            <div
              style={{
                fontSize: "0.55rem",
                color: "#555",
                marginTop: "0.2rem",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <span>
                {msg.timestamp.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
              {msg.contextIncluded && (
                <span>
                  {msg.contextIncluded.option_chain && (
                    <span style={{ color: "#26a69a" }}>OC </span>
                  )}
                  {msg.contextIncluded.technical && (
                    <span style={{ color: "#2962ff" }}>Tech</span>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div
            style={{
              alignSelf: "flex-start",
              padding: "0.35rem 0.5rem",
              borderRadius: "8px 8px 8px 0",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid #2b2b43",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: "0.25rem",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#787b86",
                  animation: "pulse 1.5s infinite",
                }}
              />
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#787b86",
                  animation: "pulse 1.5s infinite 0.3s",
                }}
              />
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: "#787b86",
                  animation: "pulse 1.5s infinite 0.6s",
                }}
              />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div
        style={{
          padding: "0.4rem",
          borderTop: "1px solid #2b2b43",
          display: "flex",
          gap: "0.3rem",
          alignItems: "flex-end",
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask about ${symbol} options...`}
          rows={1}
          style={{
            flex: 1,
            resize: "none",
            padding: "0.35rem 0.5rem",
            fontSize: "0.7rem",
            backgroundColor: "#131722",
            border: "1px solid #363a45",
            borderRadius: "4px",
            color: "#d1d4dc",
            outline: "none",
            fontFamily: "inherit",
            lineHeight: "1.4",
            maxHeight: "60px",
            overflowY: "auto",
          }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{
            padding: "0.35rem 0.6rem",
            fontSize: "0.7rem",
            backgroundColor: loading || !input.trim() ? "#363a45" : "#2962ff",
            border: "none",
            borderRadius: "4px",
            color: "#fff",
            cursor: loading || !input.trim() ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? "..." : "Send"}
        </button>
      </div>
    </div>
    </>
  );
};

export default OptionsChatBot;
